<script context='module'>
  import {
    getContext,
    setContext,
    onMount,
    createEventDispatcher,
    tick,
  } from 'svelte';

  const MODAL = Symbol('Modal');
  export const useModal = () => getContext(MODAL);
  export * from './constants';
</script>

<script>
  import {
    both,
    cond,
    complement,
    equals,
    pipe,
    nth,
    T,
  } from 'ramda';
  import { writable, derived } from 'svelte/store';
  import { useModals } from './ModalRoot.svelte';
  import useMediaQuery from '../utils/useMediaQuery';
  import {
    SMALL,
    MEDIUM,
    LARGE,
    FULL_WIDTH,
    CENTER,
  } from './constants';
  import { browser } from '$app/environment';

  const addModal = useModals();

  export let testid = undefined;
  export let size = 'medium';
  export let position = 'center';
  export let fullheight = false;
  export let transparent = false;

  const smallFullscreen = useMediaQuery('(max-width: 440px)');
  const mediumFullscreen = useMediaQuery('(max-width: 680px)');
  const largeFullscreen = useMediaQuery('(max-width: 980px)');

  const size$ = writable(size);
  $: $size$ = size;

  const position$ = writable(position);
  $: $position$ = position;

  const nthEquals = (n, value) => pipe(nth(n), equals(value));
  const fullscreen = derived([
    size$,
    position$,
    smallFullscreen,
    mediumFullscreen,
    largeFullscreen,
  ], cond([
    [nthEquals(0, SMALL), both(complement(nthEquals(1, CENTER)), nth(2))],
    [nthEquals(0, MEDIUM), both(complement(nthEquals(1, CENTER)), nth(3))],
    [nthEquals(0, LARGE), both(complement(nthEquals(1, CENTER)), nth(4))],
    [nthEquals(0, FULL_WIDTH), T],
    [T, T],
  ]));

  const dispatch = createEventDispatcher();

  let container;
  let entry;
  let mounted = false;

  onMount(() => {
    const parent = container.parentNode;
    container.remove();
    mounted = true;
    entry = addModal(container, dispatch, {
      size: size$,
      position: position$,
      fullscreen,
      transparent,
    });
    return () => {
      mounted = false;
      parent.append(container);
    };
  });

  export async function show() {
    entry.show();
    await tick();
    container.focus();
  }

  export function dismiss(value) {
    document.activeElement.blur(); // dismiss focus from modal
    entry.dismiss(value);
  }

  function escape({ key }) {
    if (key === 'Escape') {
      dismiss();
    }
  }

  setContext(MODAL, { dismiss });
</script>

<svelte:window on:keydown={escape} />

{#if browser}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
  <div class='modal-content {size} {position}' class:fullscreen={$fullscreen} class:fullheight on:click bind:this={container} data-cy={testid} tabindex='0'>
    <slot {dismiss} fullscreen={$fullscreen} />
  </div>
{/if}

<style lang="postcss">
  .modal-content {
    display: flex;
    justify-content: stretch;
    flex-direction: column;
    max-height: 90vh;
    overflow-y: auto;

    &.fullscreen {
      height: 100vh;
      max-height: unset;
    }
    
    &.fullheight {
      max-height: calc(100vh - (2 * var(--spacing--xl)));
      height: calc(100vh - (2 * var(--spacing--xl)));
    }
  }

</style>
